/**
 * Valores por defecto y hidratación de la sección Datos de la empresa (persona
 * jurídica). Espejo de @idc-forms/schemas CompanyApplicant. Mismo patrón que los
 * demás `*-defaults`: inicializa el form, mezcla el sandbox guardado (parcial) y
 * normaliza para el envío. Las reglas condicionales (detalle/otro por tipo de
 * entidad; NIT/actividad opcionales si es extranjera o sociedad en formación) viven
 * en `withCompanyConditionalRules` — mismo criterio que el FEIC interno, pero en la
 * capa web (el schema progresivo del paquete no expresa reglas cruzadas).
 */
import { z } from 'zod'
import { TYPE_ENTITY } from '@/shared/catalogs/entities'
import { NON_PROFIT_ENTITY } from '@/shared/catalogs/non-profit-entities'
import { PROFIT_ENTITY } from '@/shared/catalogs/profit-entities'
import { COUNTRY_LOCAL } from '@/shared/constants/config'
import { MESSAGES_VALIDATE } from '@/shared/constants/messages'
import { PEP_FORM_DEFAULTS, type PepFormValues } from './pep-defaults'

export interface CompanyFormValues {
  entityType: string
  entityDetail: string
  entityOther: string
  companyName: string
  tradeName: string
  registrationDate: string
  jurisdiction: string
  societyFormation: boolean
  nit: string
  economicActivity: string
  economicActivityRTU: string
  cpe: boolean
  address: {
    country: string
    department?: number
    municipality?: number
    address?: string
    zone?: number
  }
  phone: string[]
  email: string[]
  website: string
  // Constitución adaptativa por tipo de entidad (sólo se llena la que aplica).
  commercialSociety: CommercialSocietyValues
  otherSociety: OtherSocietyValues
  shareholding: boolean
  shareholdingStructure: {
    individual: ShareholderIndividualValues[]
    juridical: ShareholderJuridicalValues[]
  }
  membersOfBoard: BoardMemberValues[]
}

/** Escritura pública (o su modificación). */
export interface PublicDeedValues {
  id?: string
  number: string
  date: string
  notary: string
  typeModification?: string
}

/** Patente (sociedad o comercio). */
export interface PatentValues {
  number: string
  sheet: string
  book: string
}

/** Sociedad mercantil (entidad lucrativa). */
export interface CommercialSocietyValues {
  publicDeed: PublicDeedValues
  modificationPublicDeed: PublicDeedValues[]
  companyPatent: PatentValues
  commercialPatent: PatentValues
}

/** Documento de creación (o su modificación). */
export interface CreationDocumentValues {
  id?: string
  number: string
  date: string
  authorizer: string
  typeModification?: string
}

/** Datos de registro de otro tipo de sociedad. */
export interface RegistrationDataValues {
  number: string
  sheet: string
  book: string
  institution: string
}

/** Otro tipo de sociedad (no lucrativa / organismo internacional). */
export interface OtherSocietyValues {
  creationDocument: CreationDocumentValues
  modificationCreationDocument: CreationDocumentValues[]
  registrationData: RegistrationDataValues
}

const emptyPublicDeed = (): PublicDeedValues => ({
  number: '',
  date: '',
  notary: '',
  typeModification: ''
})
const emptyPatent = (): PatentValues => ({ number: '', sheet: '', book: '' })
const emptyCreationDocument = (): CreationDocumentValues => ({
  number: '',
  date: '',
  authorizer: '',
  typeModification: ''
})

export function emptyModificationPublicDeed(): PublicDeedValues {
  return emptyPublicDeed()
}
export function emptyModificationCreationDocument(): CreationDocumentValues {
  return emptyCreationDocument()
}

/** Miembro del órgano superior / junta directiva (form): identidad + cargo + PEP. */
export interface BoardMemberValues extends PepFormValues {
  id?: string
  name: { first: string; second?: string; others?: string }
  lastName: { first: string; second?: string; married?: string }
  identificationCard: { type: string; number: string }
  birthplace: { country: string; department?: number; municipality?: number }
  position: string
  foreign: boolean
}

/** Item vacío — miembro de junta (identidad en blanco + PEP en su default). */
export function emptyBoardMember(): BoardMemberValues {
  return {
    name: { first: '', second: '', others: '' },
    lastName: { first: '', second: '', married: '' },
    identificationCard: { type: '', number: '' },
    birthplace: { country: '' },
    position: '',
    foreign: false,
    ...PEP_FORM_DEFAULTS
  }
}

/** Accionista/socio persona individual (form). */
export interface ShareholderIndividualValues {
  id?: string
  name: { first: string; second?: string; others?: string }
  lastName: { first: string; second?: string; married?: string }
  participation: number | ''
  foreign: boolean
}

/** Accionista/socio persona jurídica (form). */
export interface ShareholderJuridicalValues {
  id?: string
  name: string
  participation: number | ''
  foreign: boolean
}

/** Item vacío — accionista individual (para append en el field array). */
export function emptyShareholderIndividual(): ShareholderIndividualValues {
  return {
    name: { first: '', second: '', others: '' },
    lastName: { first: '', second: '', married: '' },
    participation: '',
    foreign: false
  }
}

/** Item vacío — accionista jurídico. */
export function emptyShareholderJuridical(): ShareholderJuridicalValues {
  return { name: '', participation: '', foreign: false }
}

export const COMPANY_FORM_DEFAULTS: CompanyFormValues = {
  entityType: '',
  entityDetail: '',
  entityOther: '',
  companyName: '',
  tradeName: '',
  registrationDate: '',
  jurisdiction: '',
  societyFormation: false,
  nit: '',
  economicActivity: '',
  economicActivityRTU: '',
  cpe: false,
  address: { country: '', address: '', zone: undefined },
  phone: [],
  email: [],
  website: '',
  commercialSociety: {
    publicDeed: emptyPublicDeed(),
    modificationPublicDeed: [],
    companyPatent: emptyPatent(),
    commercialPatent: emptyPatent()
  },
  otherSociety: {
    creationDocument: emptyCreationDocument(),
    modificationCreationDocument: [],
    registrationData: { number: '', sheet: '', book: '', institution: '' }
  },
  shareholding: false,
  shareholdingStructure: { individual: [], juridical: [] },
  membersOfBoard: []
}

/** Mezcla el sandbox guardado (parcial) sobre los defaults para resetear el form. */
export function toCompanyFormValues(
  saved: Record<string, unknown> | null | undefined
): CompanyFormValues {
  if (!saved) return COMPANY_FORM_DEFAULTS
  const s = saved as any
  const savedStructure = s.shareholdingStructure ?? {}
  const commercial = s.commercialSociety ?? {}
  const other = s.otherSociety ?? {}
  const dc = COMPANY_FORM_DEFAULTS.commercialSociety
  const doo = COMPANY_FORM_DEFAULTS.otherSociety
  return {
    ...COMPANY_FORM_DEFAULTS,
    ...(saved as Partial<CompanyFormValues>),
    address: { ...COMPANY_FORM_DEFAULTS.address, ...s.address },
    commercialSociety: {
      publicDeed: { ...dc.publicDeed, ...commercial.publicDeed },
      modificationPublicDeed: Array.isArray(commercial.modificationPublicDeed)
        ? commercial.modificationPublicDeed
        : [],
      companyPatent: { ...dc.companyPatent, ...commercial.companyPatent },
      commercialPatent: { ...dc.commercialPatent, ...commercial.commercialPatent }
    },
    otherSociety: {
      creationDocument: { ...doo.creationDocument, ...other.creationDocument },
      modificationCreationDocument: Array.isArray(other.modificationCreationDocument)
        ? other.modificationCreationDocument
        : [],
      registrationData: { ...doo.registrationData, ...other.registrationData }
    },
    shareholdingStructure: {
      individual: Array.isArray(savedStructure.individual) ? savedStructure.individual : [],
      juridical: Array.isArray(savedStructure.juridical) ? savedStructure.juridical : []
    },
    membersOfBoard: Array.isArray(s.membersOfBoard) ? s.membersOfBoard : []
  }
}

/** La identidad de la empresa no tiene reglas condicionales: se envía tal cual. */
export function normalizeCompanyForSubmit(values: CompanyFormValues): CompanyFormValues {
  return values
}

/** ¿La jurisdicción es extranjera (distinta de la local)? */
export function isForeignJurisdiction(jurisdiction: string | undefined): boolean {
  return !!jurisdiction && jurisdiction !== COUNTRY_LOCAL.isoCode
}

/** ¿El tipo de entidad exige elegir un DETALLE (lucrativa / no lucrativa)? */
export function entityNeedsDetail(entityType: string | undefined): boolean {
  return entityType === TYPE_ENTITY.PROFIT_ENTITY || entityType === TYPE_ENTITY.NON_PROFIT_ENTITY
}

/** ¿El tipo/detalle exige el campo "Otro" (especificar)? */
export function entityNeedsOther(
  entityType: string | undefined,
  entityDetail: string | undefined
): boolean {
  return (
    entityType === TYPE_ENTITY.OTHER ||
    entityDetail === PROFIT_ENTITY.OTHER ||
    entityDetail === NON_PROFIT_ENTITY.OTHER
  )
}

/** ¿El tipo de entidad usa el bloque "otro tipo de sociedad"? (no lucrativa / organismo). */
export function entityUsesOtherSociety(entityType: string | undefined): boolean {
  return (
    entityType === TYPE_ENTITY.NON_PROFIT_ENTITY ||
    entityType === TYPE_ENTITY.INTERNATIONAL_ORGANIZATION
  )
}

/**
 * Predicados de VISIBILIDAD por tipo de entidad — fuente única compartida por el
 * formulario (pasos condicionales) y el review del asesor (grupos condicionales). Operan
 * sobre los valores de la sección `company` (tienen `entityType` / `shareholding`).
 */

/** ¿El tipo de entidad es lucrativa (sociedad mercantil)? */
export function isProfitEntity(values: Record<string, unknown>): boolean {
  return values?.entityType === TYPE_ENTITY.PROFIT_ENTITY
}

/** ¿Aplica la estructura accionaria? (lucrativa + toggle de accionistas en SÍ). */
export function showsShareholders(values: Record<string, unknown>): boolean {
  return isProfitEntity(values) && values?.shareholding === true
}

/** ¿Aplica el paso/bloque de constitución y registro? (lucrativa, no lucrativa u organismo). */
export function showsConstitution(values: Record<string, unknown>): boolean {
  return isProfitEntity(values) || entityUsesOtherSociety(values?.entityType as string | undefined)
}

/**
 * Reglas cruzadas de la empresa (las que el schema progresivo no puede expresar).
 * Envuelve el schema del nivel con un `superRefine`:
 * - NIT y actividad económica son requeridos SALVO jurisdicción extranjera o sociedad
 *   en formación (mismo criterio que el FEIC interno).
 * - El detalle del tipo de entidad es requerido para lucrativas / no lucrativas.
 * - "Otro" es requerido cuando el tipo es "Otra" o el detalle es "Otro".
 * - Constitución/registro: para lucrativas la sociedad mercantil (escritura + patentes);
 *   para no lucrativas / organismos el documento de creación + registro. Requeridos SALVO
 *   jurisdicción extranjera (mismo criterio que el FEIC interno).
 */
export function withCompanyConditionalRules(schema: z.ZodTypeAny): z.ZodTypeAny {
  return schema.superRefine((val: unknown, ctx: z.RefinementCtx) => {
    const v = (val ?? {}) as Partial<CompanyFormValues>
    const foreign = isForeignJurisdiction(v.jurisdiction)
    const lenient = foreign || v.societyFormation === true
    const empty = (s: unknown) => !String(s ?? '').trim()
    const requireAt = (path: Array<string | number>, missing: boolean) => {
      if (missing) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: MESSAGES_VALIDATE.REQUIRED })
      }
    }

    requireAt(['nit'], !lenient && empty(v.nit))
    requireAt(['economicActivity'], !lenient && empty(v.economicActivity))
    requireAt(['entityDetail'], entityNeedsDetail(v.entityType) && empty(v.entityDetail))
    requireAt(
      ['entityOther'],
      entityNeedsOther(v.entityType, v.entityDetail) && empty(v.entityOther)
    )

    // Constitución/registro — requerida sólo para jurisdicción local (no extranjera).
    if (!foreign) {
      if (v.entityType === TYPE_ENTITY.PROFIT_ENTITY) {
        const cs = v.commercialSociety
        requireAt(['commercialSociety', 'publicDeed', 'number'], empty(cs?.publicDeed?.number))
        requireAt(['commercialSociety', 'publicDeed', 'date'], empty(cs?.publicDeed?.date))
        requireAt(['commercialSociety', 'publicDeed', 'notary'], empty(cs?.publicDeed?.notary))
        for (const patent of ['companyPatent', 'commercialPatent'] as const) {
          requireAt(['commercialSociety', patent, 'number'], empty(cs?.[patent]?.number))
          requireAt(['commercialSociety', patent, 'sheet'], empty(cs?.[patent]?.sheet))
          requireAt(['commercialSociety', patent, 'book'], empty(cs?.[patent]?.book))
        }
      } else if (entityUsesOtherSociety(v.entityType)) {
        const os = v.otherSociety
        requireAt(
          ['otherSociety', 'creationDocument', 'number'],
          empty(os?.creationDocument?.number)
        )
        requireAt(['otherSociety', 'creationDocument', 'date'], empty(os?.creationDocument?.date))
        requireAt(
          ['otherSociety', 'creationDocument', 'authorizer'],
          empty(os?.creationDocument?.authorizer)
        )
        requireAt(
          ['otherSociety', 'registrationData', 'number'],
          empty(os?.registrationData?.number)
        )
        requireAt(['otherSociety', 'registrationData', 'sheet'], empty(os?.registrationData?.sheet))
        requireAt(['otherSociety', 'registrationData', 'book'], empty(os?.registrationData?.book))
        requireAt(
          ['otherSociety', 'registrationData', 'institution'],
          empty(os?.registrationData?.institution)
        )
      }
    }
  })
}
